/*
 * The /ai conversation as pure state (no React, no network).
 *
 * Two sources are merged:
 *   - server events (`GET /events`, `POST /messages`), keyed by id, kept in
 *     chronological order (createdAt, then id);
 *   - local turns, keyed by clientMessageId: a message the athlete sent whose
 *     outcome this device is still tracking (in flight, waiting for a reply the
 *     server is still producing, or failed). A turn is dropped as soon as the
 *     server shows its reply DONE.
 *
 * A turn always keeps its clientMessageId and text: every retry resends the
 * SAME body, so the server replays or resumes it instead of counting a new
 * message (contract §5.3, step 2).
 */

export const TURN_PHASES = Object.freeze({
  SENDING: "sending", // request in flight
  WAITING: "waiting", // the server is still producing the reply
  FAILED: "failed", // the server recorded a failure
  UNSENT: "unsent", // no answer reached this device: outcome unknown
  EXHAUSTED: "exhausted", // the server will not retry this message again
});

const BUSY_PHASES = new Set([TURN_PHASES.SENDING, TURN_PHASES.WAITING]);

export function createThreadState() {
  return { events: {}, order: [], nextBefore: null, loaded: false, turns: {} };
}

function sortKey(event) {
  return `${event.createdAt || ""}\u0000${event.id}`;
}

function mergeEvents(state, events) {
  if (!events?.length) return state;
  const nextEvents = { ...state.events };
  events.forEach((event) => {
    if (!event?.id) return;
    // An erasure is final on the server: a stale copy never brings the text back.
    if (nextEvents[event.id]?.redacted && !event.redacted) return;
    nextEvents[event.id] = event;
  });
  const order = Object.values(nextEvents)
    .sort((a, b) => (sortKey(a) < sortKey(b) ? -1 : 1))
    .map((event) => event.id);
  return { ...state, events: nextEvents, order };
}

// Local turns that the server now shows as answered are finished.
function settleTurns(state) {
  const turns = { ...state.turns };
  let changed = false;
  Object.values(state.events).forEach((event) => {
    if (event.type !== "MESSAGE_USER" || event.reply?.status !== "DONE") return;
    if (turns[event.clientMessageId]) {
      delete turns[event.clientMessageId];
      changed = true;
    }
  });
  return changed ? { ...state, turns } : state;
}

export function threadReducer(state, action) {
  switch (action.type) {
    case "EVENTS_LOADED": {
      // `latest` replaces the window's newest page; `older` extends it upward.
      const merged = mergeEvents(state, action.events);
      const nextBefore = action.mode === "older" || !state.loaded
        ? action.nextBefore ?? null
        : state.nextBefore;
      return settleTurns({ ...merged, nextBefore, loaded: true });
    }
    case "EVENTS_ADDED":
      return settleTurns(mergeEvents(state, action.events));
    case "TURN_STARTED":
      return {
        ...state,
        turns: {
          ...state.turns,
          [action.clientMessageId]: {
            clientMessageId: action.clientMessageId,
            text: action.text,
            startedAt: action.at,
            phase: TURN_PHASES.SENDING,
            retryable: true,
            failureCode: null,
          },
        },
      };
    case "TURN_UPDATED": {
      const turn = state.turns[action.clientMessageId];
      if (!turn) return state;
      return {
        ...state,
        turns: { ...state.turns, [action.clientMessageId]: { ...turn, ...action.patch } },
      };
    }
    case "TURN_REMOVED": {
      if (!state.turns[action.clientMessageId]) return state;
      const turns = { ...state.turns };
      delete turns[action.clientMessageId];
      return { ...state, turns };
    }
    case "RESET":
      return createThreadState();
    default:
      return state;
  }
}

export function findUserEvent(state, clientMessageId) {
  return Object.values(state.events).find(
    (event) => event.type === "MESSAGE_USER" && event.clientMessageId === clientMessageId
  ) || null;
}

// True while a message of this athlete waits for its reply: one at a time
// (the server answers a second one with COACH_MESSAGE_IN_PROGRESS).
export function isConversationBusy(state) {
  if (Object.values(state.turns).some((turn) => BUSY_PHASES.has(turn.phase))) return true;
  return Object.values(state.events).some(
    (event) => event.type === "MESSAGE_USER" && event.reply?.status === "PENDING" && !state.turns[event.clientMessageId]
  );
}

function userStatus(event, turn) {
  if (turn) return turn.phase;
  switch (event?.reply?.status) {
    case "PENDING":
      return TURN_PHASES.WAITING;
    case "FAILED":
      return TURN_PHASES.FAILED;
    default:
      return "done";
  }
}

function dayKey(iso) {
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? ""
    : `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
}

/*
 * Render items in display order: day separators, athlete messages (with their
 * delivery status), coach messages and check-ins. Local turns the server does
 * not know yet come last.
 */
export function buildThreadItems(state) {
  const items = [];
  let lastDay = null;
  const pushDay = (iso) => {
    const key = dayKey(iso);
    if (key && key !== lastDay) {
      items.push({ kind: "day", key: `day-${key}`, at: iso });
      lastDay = key;
    }
  };

  state.order.forEach((id) => {
    const event = state.events[id];
    pushDay(event.createdAt);
    if (event.type === "MESSAGE_USER") {
      const turn = state.turns[event.clientMessageId] || null;
      items.push({
        kind: "user",
        key: id,
        event,
        clientMessageId: event.clientMessageId,
        text: event.content,
        redacted: Boolean(event.redacted),
        at: event.createdAt,
        status: userStatus(event, turn),
        retryable: turn ? turn.retryable : true,
        failureCode: turn?.failureCode || event.reply?.failureCode || null,
      });
    } else if (event.type === "MESSAGE_COACH") {
      items.push({ kind: "coach", key: id, event, at: event.createdAt });
    } else if (event.type === "CHECKIN") {
      items.push({ kind: "checkin", key: id, event, at: event.createdAt });
    }
  });

  Object.values(state.turns)
    .filter((turn) => !findUserEvent(state, turn.clientMessageId))
    .sort((a, b) => (a.startedAt < b.startedAt ? -1 : 1))
    .forEach((turn) => {
      pushDay(turn.startedAt);
      items.push({
        kind: "user",
        key: `turn-${turn.clientMessageId}`,
        event: null,
        clientMessageId: turn.clientMessageId,
        text: turn.text,
        redacted: false,
        at: turn.startedAt,
        status: turn.phase,
        retryable: turn.retryable,
        failureCode: turn.failureCode,
      });
    });

  return items;
}

// The text a retry must resend: the turn's own text, else the server's copy
// (after a reload). A redacted message cannot be resent.
export function retryTextFor(state, clientMessageId) {
  const turn = state.turns[clientMessageId];
  if (turn?.text) return turn.text;
  const event = findUserEvent(state, clientMessageId);
  return event && !event.redacted && typeof event.content === "string" ? event.content : null;
}
